/**
 * 애플리케이션 설정 관리 유틸리티
 * localStorage를 사용하여 사용자 설정을 저장/불러오기
 */

const SETTINGS_KEY = 'password_wallet_settings';

// 기본 설정값
export const DEFAULT_SETTINGS = {
  // 보안 설정
  autoLogoutTime: '30', // 분 단위 (0 = 사용 안함)
  showCopyNotification: true,
  
  // 화면 설정  
  theme: 'auto', // 'light', 'dark', 'auto'
  enableAnimations: true,
  
  // 접근성 설정
  highContrast: false,
  largeFonts: false,
  
  // 앱 설정
  language: 'ko',
  lastUpdated: new Date().toISOString()
};

/**
 * 설정값을 localStorage에서 불러오기
 * @returns {Object} 설정 객체
 */
export function loadSettings() {
  try {
    const savedSettings = localStorage.getItem(SETTINGS_KEY);
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      // 기본값과 병합하여 누락된 설정값 보완
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (error) {
    console.error('설정 불러오기 실패:', error);
  }
  
  return DEFAULT_SETTINGS;
}

/**
 * 설정값을 localStorage에 저장
 * @param {Object} settings - 저장할 설정 객체
 */
export function saveSettings(settings) {
  try {
    const settingsToSave = {
      ...settings,
      lastUpdated: new Date().toISOString()
    };
    
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settingsToSave));
    
    // 설정 변경 이벤트 발생
    window.dispatchEvent(new CustomEvent('settingsChanged', {
      detail: settingsToSave
    }));
    
    return true;
  } catch (error) {
    console.error('설정 저장 실패:', error);
    return false;
  }
}

/**
 * 특정 설정값만 업데이트
 * @param {string} key - 설정 키
 * @param {*} value - 설정값
 */
export function updateSetting(key, value) {
  const currentSettings = loadSettings();
  const updatedSettings = {
    ...currentSettings,
    [key]: value
  };
  
  return saveSettings(updatedSettings);
}

/**
 * 설정 초기화 (기본값으로 복원)
 */
export function resetSettings() {
  try {
    localStorage.removeItem(SETTINGS_KEY);
    window.dispatchEvent(new CustomEvent('settingsReset'));
    return true;
  } catch (error) {
    console.error('설정 초기화 실패:', error);
    return false;
  }
}

/**
 * 설정값 검증
 * @param {Object} settings - 검증할 설정 객체
 * @returns {boolean} 유효성 여부
 */
export function validateSettings(settings) {
  const validLogoutTimes = ['0', '15', '30', '60', '120'];
  const validThemes = ['light', 'dark', 'auto'];
  
  return (
    settings &&
    typeof settings === 'object' &&
    validLogoutTimes.includes(settings.autoLogoutTime) &&
    validThemes.includes(settings.theme) &&
    typeof settings.showCopyNotification === 'boolean' &&
    typeof settings.enableAnimations === 'boolean' &&
    typeof settings.highContrast === 'boolean' &&
    typeof settings.largeFonts === 'boolean'
  );
}

/**
 * 설정 변경 리스너 추가
 * @param {Function} callback - 설정 변경시 호출될 콜백
 */
export function addSettingsListener(callback) {
  window.addEventListener('settingsChanged', callback);
}

/**
 * 설정 변경 리스너 제거
 * @param {Function} callback - 제거할 콜백
 */
export function removeSettingsListener(callback) {
  window.removeEventListener('settingsChanged', callback);
}
